<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { AliyahType, ParashahType } from 'types/types'
import { useForm } from 'vee-validate'
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import { watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import * as yup from 'yup'

import DialogTitleBar from '@/components/DialogTitleBar.vue'
import OwnerFieldRow from '@/components/OwnerFieldRow.vue'
import ParashahSelect from '@/components/ParashahSelect.vue'
import VerseSelect from '@/components/VerseSelect.vue'
import { formattedParashah, formattedRank } from '@/composables/format'
import { useDefaultOwner } from '@/composables/useDefaultOwner'
import { useUtil } from '@/composables/util'
import { YEAR_FIELD_MIN_WIDTH } from '@/constants/practiceConstants'
import store from '@/store'
import { ALIYAH_RANKS, compareYear } from '@/util/Aliyah'
import { extractRowItem, sortUserThenRoot, yearFieldSchema } from '@/util/customReading'
import { formatParashah } from '@/util/Parashah'

const logger = useLogger()
const { isStaff, selectableUsers } = useUtil(logger)

interface CustomAliyahData {
  aliyahId: string | null
  includeRootAliyot: boolean
  parashah: ParashahType | null
  tableHeaders: Array<object>
  formVisible: boolean
}

const state = reactive<CustomAliyahData>({
  aliyahId: null,
  formVisible: false,
  includeRootAliyot: false,
  parashah: null,
  tableHeaders: [],
})

const { defineField, errors, handleSubmit, meta, resetForm } = useForm({
  validationSchema: toTypedSchema(
    yup.object({
      comment: yup.string().notRequired(),
      owner: yup.string().required(),
      rank: yup.number().required(),
      section: yup.string().required(),
      variant: yup.string().required(),
      year: yearFieldSchema,
    }),
  ),
})

const [owner, ownerAttrs] = defineField('owner')
const [year, yearAttrs] = defineField('year')
const [rank, rankAttrs] = defineField('rank')
const [variant, variantAttrs] = defineField('variant')
const [section] = defineField('section')
const [comment, commentAttrs] = defineField('comment')

const applyDefaultOwner = useDefaultOwner(owner)
const aliyahVerseSelect = ref<InstanceType<typeof VerseSelect> | null>(null)

onMounted(() => {
  store.dispatch.user.fetchUser()
  state.tableHeaders = [
    { key: 'year', title: 'Year' },
    { formatter: formatParashah, key: 'parashah', title: 'Parashah' },
  ]
  if (isStaff()) {
    state.tableHeaders.push({ key: 'user.username', title: 'User' })
  }
  state.tableHeaders.push(
    { key: 'rank', title: 'Rank' },
    { key: 'section', sortable: false, title: 'Section' },
    { key: 'variant', sortable: false, title: 'Variant (full/triennial)' },
    { key: 'comment', sortable: false, title: 'Comment' },
    { key: 'action', sortable: false, title: ' ' },
  )

  watchEffect(() => {
    store.dispatch.teacher.fetchAliyot({ mine: !state.includeRootAliyot && isStaff(), parashah: null })
  })
})

const rankItems = ALIYAH_RANKS.map((rank) => ({ title: formattedRank(rank), value: rank }))

const compareAliyot = (a: AliyahType, b: AliyahType) => a.parashah.order - b.parashah.order || a.rank - b.rank || compareYear(a, b)

const sortedAliyot = computed<Array<AliyahType>>(() =>
  sortUserThenRoot(store.getters.teacher.aliyot, {
    compare: compareAliyot,
    currentUsername: store.getters.user.user!.username,
    includeRoot: state.includeRootAliyot,
    usernameOf: (aliyah) => aliyah.user.username,
  }),
)

function addAliyah() {
  resetNewAliyah()
  state.formVisible = true
}

function resetNewAliyah() {
  state.aliyahId = null
  state.parashah = null
  resetForm()

  nextTick(() => {
    if (aliyahVerseSelect.value) {
      aliyahVerseSelect.value.clear()
    }
  })

  applyDefaultOwner()
}

const handleCreateAliyah = handleSubmit(async (values) => {
  if (!state.parashah) {
    logger.debug('unexpected null parashah in handleCreateAliyah')
    return
  }

  const saved = await store.dispatch.teacher.createAliyah({
    comment: values.comment ?? null,
    id: state.aliyahId,
    parashah: state.parashah,
    rank: values.rank,
    section: values.section,
    username: values.owner,
    variant: values.variant,
    year: values.year ?? null,
  })

  if (saved) {
    state.formVisible = false
    resetNewAliyah()
  }
})

async function confirmDeleteAliyah(aliyah: AliyahType) {
  store.dispatch.dialog.show({
    message: 'Are you sure you want to delete this aliyah?',
    onAccept: () => store.dispatch.teacher.deleteAliyah({ aliyah }),
    title: 'Confirm',
  })
}

const isAliyah = (candidate: unknown): candidate is AliyahType => !!(candidate as Partial<AliyahType> | null | undefined)?.rank

function editAliyah(...args: unknown[]) {
  const aliyah = extractRowItem(isAliyah, ...args)

  if (!aliyah) {
    logger.debug('unexpected null aliyah in editAliyah')
    return
  }

  state.formVisible = true

  nextTick(() => {
    if (aliyahVerseSelect.value && aliyah.section) {
      aliyahVerseSelect.value.update(aliyah.section)
    }
    state.parashah = aliyah.parashah
    state.aliyahId = aliyah.id
    owner.value = aliyah.user.username
    year.value = aliyah.year
    rank.value = aliyah.rank
    variant.value = aliyah.variant
    section.value = aliyah.section
    comment.value = aliyah.comment ?? null
  })
}
</script>

<template>
  <v-container fluid>
    <!-- Add Aliyah -->
    <v-btn color="primary" @click="addAliyah"> Add Aliyah </v-btn>

    <v-switch v-if="isStaff()" v-model="state.includeRootAliyot" color="primary" label="Include system aliyot (slow!)" />
    <v-data-table
      :headers="state.tableHeaders"
      :items="sortedAliyot"
      no-data-text="[zero aliyot match your search; create a custom aliyah?]"
      :sort-by="[{ key: 'reading.description', order: 'asc' }]"
      @click:row="editAliyah"
    >
      <template #item.parashah="{ item }">
        <span>{{ formattedParashah(item.parashah) }}</span>
      </template>
      <template #item.action="{ item }">
        <v-btn icon variant="plain" @click.capture.stop="() => editAliyah(item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-btn color="error" icon variant="plain" @click.capture.stop="confirmDeleteAliyah(item)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- edit Aliyah -->
    <v-dialog id="newAliyah" v-model="state.formVisible" class="mb-4 pb-4" no-click-animation @keydown.esc="state.formVisible = false">
      <v-form @submit.prevent="handleCreateAliyah">
        <v-card>
          <dialog-title-bar :title="`${state.aliyahId ? 'Change' : 'Add'} Aliyah`" @close="state.formVisible = false" />
          <v-card-text>
            <owner-field-row
              v-model="owner"
              :error-messages="errors.owner"
              :items="selectableUsers"
              :owner-attrs="ownerAttrs"
              row-class="mt-3 pt-3"
            />
            <v-row>
              <v-col md="1">
                <v-text-field
                  v-model="year"
                  v-bind="yearAttrs"
                  :error-messages="errors.year"
                  label="Year"
                  placeholder="Year"
                  :style="{ minWidth: YEAR_FIELD_MIN_WIDTH }"
                  type="number"
                  variant="underlined"
                />
              </v-col>
              <v-col md="3">
                <parashah-select
                  v-model:parashah="state.parashah"
                  :clearable="false"
                  :external-set="true"
                  :reset-selection-on-multiple="false"
                />
              </v-col>
              <v-col md="2">
                <v-select
                  v-model="rank"
                  v-bind="rankAttrs"
                  :error-messages="errors.rank"
                  :items="rankItems"
                  label="Rank"
                  variant="underlined"
                />
              </v-col>

              <v-col md="2">
                <v-select
                  v-model="variant"
                  v-bind="variantAttrs"
                  :error-messages="errors.variant"
                  :items="['full', 'triennial']"
                  label="Variant"
                  return-object
                  variant="underlined"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <verse-select ref="aliyahVerseSelect" v-model:section="section" :range="true" :selected-section="section" />
              </v-col>
            </v-row>
            <v-row>
              <v-col md="3">
                <v-text-field v-model="comment" v-bind="commentAttrs" label="Comment" placeholder="" type="text" variant="underlined" />
              </v-col>
            </v-row>
            <v-row>
              <v-col md="1">
                <v-btn block color="primary" :disabled="!meta.valid" type="submit" width="140"
                  >{{ state.aliyahId ? 'Update' : 'Create' }}
                </v-btn>
              </v-col>
              <v-col md="1">
                <v-btn block variant="plain" @click="state.formVisible = false">Cancel</v-btn>
              </v-col>
              <v-col md="1">
                <v-btn block color="red-lighten-3" width="140" @click="resetNewAliyah">Reset</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-form>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.collapsed > .when-open,
.not-collapsed > .when-closed {
  display: none;
}

.trashable:hover {
  cursor: pointer;
}

.editable:hover {
  cursor: pointer;
}

.error {
  color: red;
}
</style>
